import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { rulesApi } from '../../api';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminRules.css';

const AdminRules = () => {
    const [rules, setRules] = useState([]);
    const [regulations, setRegulations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('rules');

    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [formData, setFormData] = useState({ title: '', content: '', order: 0 });

    const loadRules = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await rulesApi.list({ page: currentPage - 1, size: pageSize });
            const list = Array.isArray(data) ? data : (data?.content || []);
            const total = Array.isArray(data) ? data.length : (data?.totalElements || data?.total || list.length);
            setRules(list);
            setTotalItems(total);
        } catch (err) {
            setError('Не удалось загрузить правила');
            console.error('[AdminRules] Failed to load rules:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    const loadRegulations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await rulesApi.listRegulations();
            const list = Array.isArray(data) ? data : (data?.content || []);
            setRegulations(list);
            setTotalItems(list.length);
        } catch (err) {
            setError('Не удалось загрузить регламенты');
            console.error('[AdminRules] Failed to load regulations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'rules') {
            loadRules();
        } else {
            loadRegulations();
        }
    }, [activeTab, loadRules, loadRegulations]);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setFormData({
                title: item.title || '',
                content: item.content || '',
                order: item.order || 0,
            });
        } else {
            setFormData({ title: '', content: '', order: 0 });
        }
        setShowModal(true);
        setError(null);
    };

    const handleSave = async () => {
        if (!formData.title.trim() || !formData.content.trim()) {
            setError('Заполните название и содержание');
            return;
        }
        try {
            setActionLoading(true);
            if (activeTab === 'rules') {
                if (editingItem) {
                    await rulesApi.update(editingItem.id, formData);
                } else {
                    await rulesApi.create(formData);
                }
                await loadRules();
            } else {
                if (editingItem) {
                    await rulesApi.updateRegulation(editingItem.id, formData);
                } else {
                    await rulesApi.createRegulation(formData);
                }
                await loadRegulations();
            }
            setShowModal(false);
            setEditingItem(null);
        } catch (err) {
            setError(`Не удалось сохранить: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Удалить "${item.title}"?`)) return;
        try {
            setActionLoading(true);
            if (activeTab === 'rules') {
                await rulesApi.remove(item.id);
                await loadRules();
            } else {
                await rulesApi.removeRegulation(item.id);
                await loadRegulations();
            }
        } catch (err) {
            setError(`Не удалось удалить: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        try {
            setActionLoading(true);
            await rulesApi.updateStatus(item.id, newStatus);
            await loadRules();
        } catch (err) {
            setError(`Не удалось изменить статус: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'ACTIVE': return 'success';
            case 'INACTIVE': return 'secondary';
            case 'PUBLISHED': return 'success';
            case 'DRAFT': return 'warning';
            default: return 'secondary';
        }
    };

    const items = activeTab === 'rules' ? rules : regulations;

    if (loading && items.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление правилами и регламентами</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-rules-tabs">
                <Button
                    variant={activeTab === 'rules' ? 'primary' : 'outline-primary'}
                    onClick={() => { setActiveTab('rules'); setCurrentPage(1); }}
                    className="me-2"
                >
                    Правила
                </Button>
                <Button
                    variant={activeTab === 'regulations' ? 'primary' : 'outline-primary'}
                    onClick={() => { setActiveTab('regulations'); setCurrentPage(1); }}
                >
                    Регламенты
                </Button>
            </div>

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                    </Form.Select>
                    <Button variant="primary" onClick={() => handleOpenModal()}>
                        + Добавить
                    </Button>
                </div>

                <Table responsive hover className="admin-rules-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Название</th>
                            <th>Содержание</th>
                            <th>Статус</th>
                            {activeTab === 'rules' && <th>Порядок</th>}
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center text-muted">
                                    Ничего не найдено
                                </td>
                            </tr>
                        ) : (
                            items.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td><strong>{item.title}</strong></td>
                                    <td>
                                        <div className="admin-rules-content">
                                            {item.content?.substring(0, 100)}
                                            {item.content?.length > 100 ? '...' : ''}
                                        </div>
                                    </td>
                                    <td>
                                        <Badge bg={getStatusBadge(item.status)}>
                                            {item.status || 'ACTIVE'}
                                        </Badge>
                                    </td>
                                    {activeTab === 'rules' && <td>{item.order || 0}</td>}
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleOpenModal(item)}
                                            disabled={actionLoading}
                                        >
                                            Редактировать
                                        </Button>
                                        {activeTab === 'rules' && (
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleToggleStatus(item)}
                                                disabled={actionLoading}
                                            >
                                                {item.status === 'ACTIVE' ? 'Выключить' : 'Включить'}
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDelete(item)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                {activeTab === 'rules' && (
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                    />
                )}
            </HrnbCard>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Редактировать' : 'Добавить'} {activeTab === 'rules' ? 'правило' : 'регламент'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Название</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Введите название"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Содержание</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                placeholder="Введите содержание"
                            />
                        </Form.Group>
                        {activeTab === 'rules' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Порядок</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.order}
                                    onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                                />
                            </Form.Group>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={actionLoading}>
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminRules;
